// Shared API helper. The app had no central fetch wrapper (every page called fetch() raw and many
// skipped the r.ok check), so an entitlement wall (HTTP 402) or no-market (403) response — a JSON
// OBJECT, not the array/shape a list page expects — flowed straight into .reduce/.map and crashed
// the page (e.g. the demo Buyers Database).
//
// apiGet/apiSend parse the body, and on a non-ok response THROW a typed error instead of returning
// the error body. Pages catch these and render a friendly state (locked panel / limit notice /
// connect-MLS prompt) — list code never sees an error body.

// 402 — feature locked on the plan, or a monthly cap reached.
export class EntitlementError extends Error {
  constructor(payload) {
    super(payload?.error || 'This feature is not available on your plan');
    this.name = 'EntitlementError';
    const e = payload?.entitlement || {};
    this.reason = e.reason || 'locked';        // 'locked' | 'limit'
    this.feature = e.feature || null;
    this.plan = e.plan || null;
    this.limit = e.limit;
    this.used = e.used;
    this.overLimit = payload?.over_limit || null; // { status:'pending'|'approved'|..., request_id }
    this.httpStatus = 402;
  }
}

// 403 {code:'no_market'} — a non-market-owner org tried to trigger an MLS scan/sync (Phase 5).
export class NoMarketError extends Error {
  constructor(payload) {
    super(payload?.error || 'No MLS market is connected for your organization yet');
    this.name = 'NoMarketError';
    this.code = 'no_market';
    this.httpStatus = 403;
  }
}

async function parse(res) {
  const data = await res.json().catch(() => ({}));
  if (res.ok) return data;
  if (res.status === 402) throw new EntitlementError(data);
  if (res.status === 403 && data && data.code === 'no_market') throw new NoMarketError(data);
  const err = new Error(data.error || `Request failed (${res.status})`);
  err.httpStatus = res.status;
  err.payload = data;   // full error body, so callers can read extra fields (e.g. existing_deal_id on 409)
  throw err;
}

export function apiGet(path) {
  return fetch(path, { credentials: 'include' }).then(parse);
}

export function apiSend(path, { method = 'POST', body, headers } = {}) {
  return fetch(path, {
    method,
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...(headers || {}) },
    body: body == null ? undefined : (typeof body === 'string' ? body : JSON.stringify(body)),
  }).then(parse);
}

// Classify a caught error into a render-state, or null if it's an ordinary error.
//   { kind: 'locked' | 'limit' | 'no_market', error }
export function entitlementState(err) {
  if (err instanceof EntitlementError) return { kind: err.reason, error: err };
  if (err instanceof NoMarketError) return { kind: 'no_market', error: err };
  return null;
}
